import React, { memo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { validators } from 'investira.sdk';
import { Icon, Stack, Alert, LoadingButton } from 'investira.react.components';
import { PageTemplate } from '../../components/templates';
import { useRelatorioContext } from '../../contexts/RelatorioContext';
import { OUTPUT } from '../../enums';
import withOutput from '../../hoc/withOutput';
import services from '../../services';
import ConsultaDuration from './_components/ConsultaDuration';
import ConsultaEnquadramento from './_components/ConsultaEnquadramento';
import ConsultaVar from './_components/ConsultaVar';
import ConsultaSkeleton from './_components/ConsultaSkeleton';
import ConsultaError from './_components/ConsultaError';
import ConsultaProcess from './_components/ConsultaProcess';
import ConsultaStressTest from './_components/ConsultaStressTest';
import ConsultaConferenciaCustodiante from './_components/ConsultaConferenciaCustodiante';
import { Preview } from '../../components/organisms';

const ReportsConsulta = memo(props => {
  const params = useParams();
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewParams, setPreviewParams] = useState(null);
  const [previewFileName, setPreviewFileName] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const { actions } = useRelatorioContext();

  const OUTPUTS = {
    duration: ConsultaDuration,
    enquadramento: ConsultaEnquadramento,
    var: ConsultaVar,
    stresstest: ConsultaStressTest,
    conferenciacustodiante: ConsultaConferenciaCustodiante
  };

  function resolveOutputMeta(pOutput) {
    return {
      output_cache_id: pOutput?.output_cache_id || params.id,
      files_name: pOutput?.files_name || props.filesName,
      files_created: pOutput?.files_created || props.filesCreated
    };
  }

  function handleDownloadClick(pOutput, pType) {
    const { output_cache_id, files_name, files_created } = resolveOutputMeta(pOutput);

    if (output_cache_id && !validators.isEmpty(files_created)) {
      setIsLoading(true);
      services.outputs.download(
        {
          output_cache_id,
          type: pType
        },
        rRes => {
          let xOutputFilename = `${files_name}.${pType}`;
          const xFileURL = URL.createObjectURL(rRes.data);
          let xLinkElem = document.createElement('a');
          xLinkElem.href = xFileURL;
          xLinkElem.download = xOutputFilename;
          xLinkElem.click();
          URL.revokeObjectURL(xFileURL);
        },
        rErr => {
          console.log(rErr);
          props.onMessageError('Não foi possível efetuar o donwload do arquivo');
        },
        () => {
          setIsLoading(false);
        }
      );
    }
  }

  function handlePreviewClick(pOutput, pType) {
    const { output_cache_id, files_name, files_created } = resolveOutputMeta(pOutput);

    if (output_cache_id && !validators.isEmpty(files_created)) {
      setPreviewParams({
        output_cache_id,
        type: pType
      });
      setPreviewFileName(`${files_name}.${pType}`);
      setIsPreviewOpen(true);
    }
  }

  function handleClosePreview() {
    setIsPreviewOpen(false);
    setPreviewParams(null);
    setPreviewFileName(null);
  }

  useEffect(() => {
    actions.handleOutputUpdate(params.output);
  }, [params.output]);

  const ConsultaComponent = OUTPUTS[params.output];

  return (
    <>
      <PageTemplate
        title={'Relatório'}
        scrollable={true}
        subtitle={OUTPUT[params.output].descricao}
        headerRenderActions={
          <Stack direction="row" spacing={2}>
            {validators.isEmpty(props.data) ? (
              <></>
            ) : !validators.isEmpty(props.filesErrors) ? (
              <Alert variant="outlined" severity="warning" sx={{ px: 1, py: 0 }}>
                Ocorreu um erro na geração do relátorio.
              </Alert>
            ) : (
              <Stack direction="row" spacing={1}>
                <Stack direction="row" spacing={1}>
                  <LoadingButton
                    disabled={
                      !props.isFinished ||
                      !validators.isEmpty(props.filesErrors) ||
                      validators.isEmpty(props.filesCreated)
                    }
                    loading={!props.isFinished}
                    variant="contained"
                    color="primary"
                    startIcon={<Icon iconName="eye" size={16} />}
                    size="small"
                    sx={{ px: 2 }}
                    onClick={() => handlePreviewClick(props.data, 'pdf')}>
                    PDF
                  </LoadingButton>
                  <LoadingButton
                    disabled={
                      !props.isFinished ||
                      !validators.isEmpty(props.filesErrors) ||
                      validators.isEmpty(props.filesCreated) ||
                      isLoading
                    }
                    loading={!props.isFinished || isLoading}
                    variant="contained"
                    color="primary"
                    startIcon={<Icon iconName="download" size={16} />}
                    size="small"
                    sx={{ px: 2 }}
                    onClick={() => handleDownloadClick(props.data, 'xlsx')}>
                    XLSX
                  </LoadingButton>
                </Stack>
              </Stack>
            )}
          </Stack>
        }>
        {props.isProcessing && !props.completed ? (
          <ConsultaProcess />
        ) : (validators.isEmpty(props.data) ||
            validators.isEmpty(props.data.response) ||
            validators.isEmpty(props.data.response.detalhe) ||
            validators.isEmpty(props.data.response.resumo)) &&
          !props.isProcessing ? (
          <ConsultaError outputId={params.id} />
        ) : validators.isEmpty(props.data) && props.isProcessing && props.completed ? (
          <ConsultaSkeleton />
        ) : (
          <ConsultaComponent data={props.data} />
        )}
      </PageTemplate>
      <Preview
        open={isPreviewOpen}
        title={previewFileName || 'Pré-visualização'}
        fileName={previewFileName}
        previewParams={previewParams}
        onClose={handleClosePreview}
      />
    </>
  );
});

ReportsConsulta.displayName = 'ReportsConsulta';

export default withOutput(ReportsConsulta);
